/**
 * 회원 데이터 액세스 계층
 * API 호출 로직을 캡슐화
 */

import { apiClient } from '../../../infrastructure/api';
import { faceRegistrationApi } from '../../../services/faceRegistrationApi';
import type {
  Member,
  MemberListResponse,
  MemberDetailResponse,
  NewMemberData,
} from '../models';

/** 복구 때 바꿀 수 있는 항목 (서버 RESTORE_FIELDS 와 같게) */
export type RestoreField = 'name' | 'school' | 'grade' | 'gender' | 'parent_phone_1' | 'parent_phone_2';

export interface DeletedMemberInfo {
  id: string;
  name: string;
  phone: string;
  user_number?: string;
  current?: Partial<Record<RestoreField, string | null>>;
}

/** 회원 추가가 퇴원한 기존 회원과 겹칠 때 — 화면이 비교 창을 띄운다 */
export class DeletedMemberExistsError extends Error {
  constructor(public deletedMember: DeletedMemberInfo, public memberData: NewMemberData) {
    super('이전에 퇴원한 회원이 있습니다.');
    this.name = 'DeletedMemberExistsError';
  }
}

export class MemberRepository {
  /**
   * 회원 목록 조회 (서버 페이지네이션)
   */
  async getMembers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    membershipType?: string;
    withdrawn?: boolean;  // true면 퇴원생(탈퇴) 목록
    sort?: string;  // registered_desc(기본)/registered_asc/name/name_desc/seat_asc/seat_desc
  }): Promise<MemberListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(params?.page || 1));
      queryParams.append('limit', String(params?.limit || 20));

      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.status && params.status !== 'all') {
        queryParams.append('status', params.status);
      }
      if (params?.membershipType && params.membershipType !== 'all') {
        queryParams.append('membershipType', params.membershipType);
      }
      if (params?.withdrawn) {
        queryParams.append('withdrawn', 'true');
      }
      if (params?.sort) {
        queryParams.append('sort', params.sort);
      }

      const response = await apiClient.get<MemberListResponse>(`/v1/owner/members?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('회원 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 회원 전체 목록 조회 (레거시 호환용)
   */
  async getAllMembers(): Promise<Member[]> {
    try {
      const response = await apiClient.get<MemberListResponse>('/v1/owner/members?limit=9999');
      return response.data.members || [];
    } catch (error) {
      console.error('회원 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 회원 상세 정보 조회 (출입 기록 포함)
   */
  async getMemberDetail(memberIdentifier: string): Promise<MemberDetailResponse> {
    try {
      const response = await apiClient.get<MemberDetailResponse>(
        `/v1/owner/members/${memberIdentifier}`
      );
      return response.data;
    } catch (error) {
      console.error('회원 상세 정보 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 새 회원 등록
   */
  async createMember(memberData: NewMemberData): Promise<Member> {
    try {
      const response = await apiClient.post<Member>('/v1/owner/members', {
        name: memberData.name,
        phone: memberData.phone,
        parent_phone_1: memberData.parent_phone_1,
        parent_phone_2: memberData.parent_phone_2,
        school: memberData.school,
        grade: memberData.grade,
        class_name: memberData.class_name,   // (버그수정) 폼에서 받던 '반'이 전송 누락되던 것 복구
        gender: memberData.gender,
        birth_date: memberData.birth_date,     // 회의 2026-07-04: 생년월일
        member_category: memberData.member_category,  // 회의 2026-07-04: 회원구분
        assigned_seat: memberData.assigned_seat,
        seat_id: memberData.seat_id,  // 회원추가 시 빈좌석 즉시 배정
        zone_id: memberData.zone_id,
      });
      return response.data;
    } catch (error: any) {
      console.error('회원 등록 실패:', error);

      // 409: 퇴원(삭제)한 기존 회원 — 복구 여부와 바꿀 항목은 화면의 비교 창에서 고른다 (2026-10-28 대표 지시)
      //   종전엔 window.confirm 한 번에 새 값으로 통째로 덮었다(우장산 홍설하 번호가 '010' 으로 덮인 원인).
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        if (detail?.code === 'DELETED_MEMBER_EXISTS') {
          throw new DeletedMemberExistsError(detail.deleted_member, memberData);
        }
      }

      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message;
      throw new Error(typeof errorMessage === 'object' ? errorMessage.message : errorMessage);
    }
  }

  /**
   * 삭제된 회원 복구 (재활성화)
   */
  async restoreMember(
    memberId: string,
    memberData: NewMemberData,
    applyFields: RestoreField[] = []
  ): Promise<Member> {
    try {
      // 🔴 applyFields 에 든 항목만 서버가 바꾼다 — 나머지는 값을 보내도 기존 그대로
      const response = await apiClient.post<any>(`/v1/owner/members/${memberId}/restore`, {
        apply_fields: applyFields,
        name: memberData.name,
        school: memberData.school,
        grade: memberData.grade,
        gender: memberData.gender,
        parent_phone_1: memberData.parent_phone_1,
        parent_phone_2: memberData.parent_phone_2,
      });
      return response.data.member;
    } catch (error: any) {
      console.error('회원 복구 실패:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      throw new Error(typeof errorMessage === 'object' ? errorMessage.message : errorMessage);
    }
  }

  /**
   * 회원 얼굴 등록 삭제
   */
  async deleteMemberFace(member: Member): Promise<void> {
    try {
      // member_id 또는 memberNumber를 사용하여 얼굴 삭제
      const faceUserId = member.member_id || member.memberNumber || member.id;

      await faceRegistrationApi.deleteFace(faceUserId);
    } catch (error) {
      console.error('얼굴 삭제 실패:', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스
export const memberRepository = new MemberRepository();
